// ============================================================
// Email ingest — commit client (browser -> ingest-email-corpus Edge Function)
// Applies user-toggled redactions to message bodies, hashes the source file
// (integrity anchor), and POSTs one validated thread per call.
// ============================================================

import { getSupabase } from "../supabase";
import { applyRedactions } from "./piiScan";
import type { ParsedEmailThread } from "./types";

// Dogfood default scope: Co-Defendant Defense remit (non-public). NOT court_public.
// TODO: make dynamic per case once multi-remit selection ships.
export const DEFAULT_SCOPE_CODEFENDANT_DEFENSE = "33333333-3333-4333-8333-333333333301";

export async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Produce a commit-ready copy of the thread with redactions applied to bodies. */
export function redactThreadForCommit(thread: ParsedEmailThread): ParsedEmailThread {
  return {
    ...thread,
    messages: thread.messages.map((m) => ({
      ...m,
      body: m.pii_flags.some((f) => f.redact) ? applyRedactions(m.body, m.pii_flags) : m.body,
    })),
  };
}

export interface IngestResult { job_id: string; thread_id: string; status: string; }

/** Ingest one thread. caseId binds the evidence; scopeIds default to the
 *  Co-Defendant Defense remit. sourceFile is hashed as the integrity anchor. */
export async function ingestThread(
  caseId: string,
  thread: ParsedEmailThread,
  sourceFile: File,
  scopeIds: string[] = [DEFAULT_SCOPE_CODEFENDANT_DEFENSE],
): Promise<IngestResult> {
  const supabase = getSupabase();
  if (!supabase) throw new Error("Supabase not configured");
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not signed in");

  const source_sha256 = await sha256Hex(sourceFile);
  const committed = redactThreadForCommit(thread);

  const baseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  const res = await fetch(`${baseUrl}/functions/v1/ingest-email-corpus`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
      apikey: anonKey,
    },
    body: JSON.stringify({
      case_id: caseId,
      scope_ids: scopeIds,
      // Drive is the authoritative archive; bucket-backed retrieval wires with the Retriever.
      source_uri: `drive:${sourceFile.name}`,
      source_sha256,
      thread: committed,
    }),
  });

  const text = await res.text();
  if (!res.ok) {
    let detail = text;
    try { detail = JSON.parse(text).error ?? text; } catch { /* keep raw */ }
    throw new Error(`Ingest failed (HTTP ${res.status}): ${detail}`);
  }
  const data = JSON.parse(text) as IngestResult;
  if (!data.job_id) throw new Error("Ingest returned no job_id");
  return data;
}
